import { NO_VALUE_PLACEHOLDER } from "../../common/ConstantValues";
import { ITagMetadata, TagSource, TagValueType } from "../data_point/models/TagsModels";
import { Tags } from "../data_point/tags/TagCatalog";
import { AgricultureTagGroups } from "../data_point/tags/AgricultureTagGroups";
import {
  AgriSensorManufacturerType,
  ISiteMetadata,
  ISitesMetadataDictionary,
  WeatherSensorFeatures,
  SiteTrackerType,
} from "../sites/SiteModels";
import { getConverterFunction } from "./TagValueConverters";
import { formatDuration } from "../../common/StringUtils";

const CommonAggregatedId = 32767;
const MaxHoursOfSun = 12;

export const invalidTagMetadata = {
  displayName: NO_VALUE_PLACEHOLDER,
  unitName: NO_VALUE_PLACEHOLDER,
  description: NO_VALUE_PLACEHOLDER,
  uniqueName: "",
} as ITagMetadata;

function createTagMetadata(
  uniqueName: string,
  tagValueType: TagValueType,
  unitName: string,
  displayName: string,
  description: string,
  low: number,
  high: number | undefined,
  tagSource: TagSource
): ITagMetadata {
  return { uniqueName, tagValueType, unitName, displayName, description, low, high, tagSource };
}

/** Aliases keep the original tag's value meaning even when their displayed name changes. */
export function getTagValueFormatter(metadata: ITagMetadata): (value: number) => string {
  if (metadata.origin) {
    return getTagValueFormatter(metadata.origin.tag);
  }
  if (metadata.tagValueType === TagValueType.Duration) {
    return formatDuration;
  }
  return metadata.uniqueName ? getConverterFunction(metadata.uniqueName) : () => NO_VALUE_PLACEHOLDER;
}

export function getSiteTagsMetadata(smd: ISiteMetadata): ITagMetadata[] {
  const ret: ITagMetadata[] = [];

  const numberOfTrackers = Object.keys(smd.trackers).length;
  if (numberOfTrackers > 0) {
    ret.push(...generateCalculatedTrackerStateCountTagMetadata());
    ret.push(
      ...selectMany(
        Object.keys(smd.trackers).map((trackerId) =>
          generateTrackerTagMetadata(parseInt(trackerId, 10), smd.trackerType)
        )
      )
    );
    ret.push(...generateCalculatedTrackerStatusTags(numberOfTrackers));
  }

  const numberOfPowerMetersDevices = Object.keys(smd.powerMeters).length;
  if (numberOfPowerMetersDevices > 0) {
    ret.push(
      ...selectMany(
        Object.keys(smd.powerMeters).map((deviceId) =>
          generatePowerMeterTagMetadata(parseInt(deviceId, 10), true)
        )
      )
    );
    //Hack aggragated
    ret.push(...generatePowerMeterTagMetadata(CommonAggregatedId, false));
    //prettier-ignore
    ret.push(createTagMetadata(Tags.Portal.PowerMeter.AggrStatus.logicalName, TagValueType.Enum, "",
      "Power Meter Status", "", 0, numberOfPowerMetersDevices, TagSource.Calculated));
  }

  const numberOfInverterDevices = Object.keys(smd.inverters).length;
  const aggregatedInverterPowerMax = roundUp(
    Object.values(smd.inverters).reduce((s, c) => s + c, 0) * 1.1
  );
  if (numberOfInverterDevices > 0) {
    ret.push(...generateInvertersTagMetadata(CommonAggregatedId, false, aggregatedInverterPowerMax));
    ret.push(
      ...selectMany(
        Object.entries(smd.inverters).map(([deviceId, dcCapacity]) =>
          generateInvertersTagMetadata(parseInt(deviceId, 10), true, roundUp(dcCapacity))
        )
      )
    );
    ret.push(...generateCalculatedInverterStatusTags(numberOfInverterDevices));
    ret.push(...generateCalculatedPowerTags(aggregatedInverterPowerMax * MaxHoursOfSun));
  }
  ret.push(...generateAlgoPerformanceTagMetadata(aggregatedInverterPowerMax));

  const numberOfWeatherDevices = Object.keys(smd.weatherSensors).length;
  if (numberOfWeatherDevices > 0) {
    //Total
    const allWeatherSensorFeatureFlags = Object.values(WeatherSensorFeatures).reduce(
      (agg: number, val) => agg | (val as number),
      0
    );
    ret.push(...generateWeatherTags(CommonAggregatedId, allWeatherSensorFeatureFlags, false));
    for (const [deviceId, deviceInfo] of Object.entries(smd.weatherSensors)) {
      ret.push(...generateWeatherTags(parseInt(deviceId, 10), deviceInfo.features, true));
    }
    ret.push(...generateCalculatedWeatherTags(numberOfWeatherDevices));
  }

  if (smd.upsExists) {
    //prettier-ignore
    ret.push(createTagMetadata(Tags.McsState.UpsEmergencyStatus.master, TagValueType.Enum, "Undefined=0; Active=1; Inactive=2; Unknown=3;",
      "Ups Emergency Status", "", 0, 3, TagSource.Storage));
  }

  const numberOfAgriDevices = Object.keys(smd.agriSensors).length;
  if (numberOfAgriDevices > 0) {
    for (const sensorInfo of new Set(Object.values(smd.agriSensors))) {
      generateTagsForAgriDevice(ret, CommonAggregatedId, sensorInfo.manufacturerType);
    }
    for (const [sensorId, sensorInfo] of Object.entries(smd.agriSensors)) {
      generateTagsForAgriDevice(ret, parseInt(sensorId, 10), sensorInfo.manufacturerType);
    }
  }

  //prettier-ignore
  ret.push(createTagMetadata(Tags.Weather.EstimatedDni.master, TagValueType.Float, "kWh", "DNI", "Estimation of the DNI using Erbs algorithm", 0, 1500, TagSource.Storage));

  for (const id of smd.sprinklersIds) {
    ret.push(...generateFaucetOpeningRatioTagMetadata(id));
  }

  ret.push(...generateMcsTagMetadata());
  return ret;
}

export function addAliasedTags(sitesMetadata: ISitesMetadataDictionary): void {
  for (const [siteId, siteMetadata] of Object.entries(sitesMetadata)) {
    for (const [uniqueName, alias] of Object.entries(siteMetadata.tagAliases)) {
      const originSite = alias.siteName || siteId;
      const originTagMetadata = sitesMetadata[originSite]?.tags[alias.tagName];
      if (!originTagMetadata) {
        console.warn(`Could not find origin tag for alias ${uniqueName} in site ${siteId}`, {
          alias,
        });
        continue;
      }
      sitesMetadata[siteId].tags[uniqueName] = {
        ...originTagMetadata,
        uniqueName: uniqueName,
        origin: {
          siteName: originSite,
          tag: originTagMetadata,
        },
      };
    }
  }
}

const roundUp = (value: number, roundScale = 10) => value - (value % roundScale) + roundScale;

function selectMany<T>(arr: T[][]): T[] {
  return arr.reduce((a, b) => a.concat(b), []);
}

//prettier-ignore
const generateCalculatedTrackerStateCountTagMetadata = () =>
  [
    createTagMetadata("VirtualTags:Trackers:StateCount:Tracking", TagValueType.Int, "#", "Trackers tracking", "Trackers in Tracking State", 0,
      100, TagSource.Calculated),
    createTagMetadata("VirtualTags:Trackers:StateCount:Maintenance", TagValueType.Int, "#", "Trackers in maintenance",
      "Trackers in Maintenance State", 0, 100, TagSource.Calculated),
  ];

//prettier-ignore
const generateTrackerTagMetadata = (id: number, trackerType?: SiteTrackerType) =>
  [
    createTagMetadata(Tags.TrackerStatus.CurrentState.device(id), TagValueType.Enum, "", "Field State", "", 0, 11, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.ShadingRatio.device(id), TagValueType.Float, "", "Shading Ratio", "", 0, 100, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.CurrentElevation.device(id), TagValueType.Float, "°", "Average tracker elevation", "Average angle of trackers", -70, 70, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.TargetElevation.device(id), TagValueType.Float, "°", "Target Elevation", "Target angle of trackers", -70, 70, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.ErrorType.device(id), TagValueType.Enum, "Enum", "Error flags", "", 0, 32, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.TrackerAutoFirmwareUpdateStatus.device(id), TagValueType.Enum, "", "Auto FW Update Status", "", 0, 5, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.Uptime.device(id), TagValueType.Duration, "", "Uptime", "", 0, undefined, TagSource.Storage),
    createTagMetadata(Tags.TrackerStatus.InputPotential.device(id), TagValueType.Float, "", "Input Potential", "", 6, 32, TagSource.Storage),
    ...(trackerType === SiteTrackerType.SPT ? [createTagMetadata(Tags.TrackerStatus.BatteryVoltage.device(id), TagValueType.Float, "V", "Battery Voltage", "", 0, 15, TagSource.Storage)] : []),
  ];

//prettier-ignore
const generateCalculatedTrackerStatusTags = (numberOfTrackers: number) =>
  [
    createTagMetadata(Tags.Portal.TrackerStatus.SystemStatus.logicalName, TagValueType.Enum, "", "Trackers status", "Status of the Site Trackers", 0,
      numberOfTrackers, TagSource.Calculated),
  ];

//prettier-ignore
const generateFaucetOpeningRatioTagMetadata = (id: number) =>
  [
    createTagMetadata(Tags.CleaningFaucetOpeningRatio.OpeningRatio.device(id), TagValueType.Float, "%", "Faucet Opening Ratio", "", 0,
      100, TagSource.Storage),
  ];

//prettier-ignore
const generatePowerMeterTagMetadata = (id: number, includeErrors: boolean) => {
  const metadataList =
    [
      createTagMetadata(Tags.PowerMeter.ProductionTotal.device(id), TagValueType.Float, "kWh", "Total Production", "",
        0, 1000, TagSource.Storage),
      createTagMetadata(Tags.PowerMeter.ProductionDelta.device(id), TagValueType.Float, "kWh", "Total Consumption", "",
        0, 10, TagSource.Storage)
    ];
  if (includeErrors) {
    metadataList.push(createTagMetadata(Tags.PowerMeter.ErrorType.device(id), TagValueType.Enum, "Enum", "Error flags", "",
      0, 0xffffffff, TagSource.Storage));
  }
  return metadataList;
};

//prettier-ignore
const generateInvertersTagMetadata = (id: number, includeErrors: boolean, maxValue: number) => {
  const metadataList = [
      createTagMetadata(Tags.Portal.Inverter.AcPower.device(id), TagValueType.Float, "kW", "AC Power",
        "Live Output Power of All the Inverters", 0, maxValue, TagSource.Calculated),
      createTagMetadata(Tags.Portal.Inverter.DcPower.device(id), TagValueType.Float, "kW", "DC Power",
        "Live Input of All the Inverters", 0, maxValue, TagSource.Storage)
    ];
  if (includeErrors) {
    metadataList.push(createTagMetadata(Tags.Inverter.ErrorType.device(id), TagValueType.Enum, "Enum", "Error flags", "",
      0, 4294967300, TagSource.Storage));
    metadataList.push(createTagMetadata(Tags.Inverter.InternalAirTemperature.device(id), TagValueType.Float, "°C",
      "Internal Air Temperature", "Internal air temperature reported by the inverter", -50, 100, TagSource.Storage));
  }
  return metadataList;
};

//prettier-ignore
const generateCalculatedInverterStatusTags = (maxLimit: number) =>
  [
    createTagMetadata(Tags.Portal.Inverter.SystemStatus.logicalName, TagValueType.Enum, "", "Inverters status", "Status of the Site Inverters", 0,
      maxLimit, TagSource.Calculated),
  ];

//prettier-ignore
const generateCalculatedPowerTags = (maxDailyPower: number) =>
  [
    createTagMetadata(Tags.Portal.DailyProduction.AcPower.logicalName, TagValueType.Float, "kWh", "AC Power",
      "Accumulated Daily Output of All Inverters", 0, maxDailyPower, TagSource.Calculated),
    createTagMetadata(Tags.Portal.DailyProduction.DcPower.logicalName, TagValueType.Float, "kWh", "DC Power",
      "Calcluated tag for daily spec", 0, maxDailyPower, TagSource.Calculated),
    createTagMetadata(Tags.Portal.DailyProduction.Potential.logicalName, TagValueType.Float, "kWh", "Potential AC Power",
      "Accumulated potential Daily Output of All Inverters", 0, maxDailyPower, TagSource.Calculated),
    createTagMetadata(Tags.Portal.DailyProduction.Sp.logicalName, TagValueType.Float, "kWh/kWp", "Specific Production",
      "Ratio of daily DC Power to installed panels' power", 0, 13, TagSource.Calculated),
    createTagMetadata("VirtualTags:Performance:PerformanceIndex", TagValueType.Float, "%", "Perf index",
      "Ratio of Actual DC Power to Potential DC Power", 0, 110, TagSource.Calculated),
    createTagMetadata(Tags.FrontendCalculated.CurrentPerformanceIndex.logicalName, TagValueType.Float, "%", "Current Performance Index",
      "Ratio of Actual AC Power to Potential AC Power", 0, 110, TagSource.FrontendCalculation),
  ];

//prettier-ignore
const generateWeatherTags = (id: number, flag: number, includeErrors: boolean) => {
  const ret: ITagMetadata[] = [];

  if ((flag & WeatherSensorFeatures.WindSpeed) != 0) {
   ret.push(createTagMetadata(Tags.Portal.Weather.WindSpeed.device(id), TagValueType.Float,
         "m/s", "Wind Speed", "Highest Wind Speed currently reported", 0, 20, TagSource.Calculated));
  }
  if ((flag & WeatherSensorFeatures.WindDirection) != 0) {
    ret.push(createTagMetadata(Tags.Weather.WindDirection.device(id), TagValueType.Float, "°",
      "Wind Direction", "", 0, 360, TagSource.Storage));
  }

  if ((flag & WeatherSensorFeatures.Irradiation) != 0) {
    ret.push(createTagMetadata(Tags.Weather.GlobalIrradiance.device(id), TagValueType.Float, "w/m²", "GHI",
      "Global Horizontal Irradiance", 0, 1500, TagSource.Storage));
  }

  if ((flag & WeatherSensorFeatures.Temperature) != 0) {
    ret.push(createTagMetadata(Tags.Weather.AmbientAirTemp.device(id), TagValueType.Float, "°c",
      "Temperature", "Ambient Temperature", -50, 50, TagSource.Storage));
  }

  if ((flag & WeatherSensorFeatures.IrradiationInstalledOnTracker) != 0) {
    ret.push(createTagMetadata(Tags.Weather.PlaneOfArrayIrradiance.device(id), TagValueType.Float, "w/m²",
      "GTI", "", 0, 1500, TagSource.Storage));
  }
   if ((flag & WeatherSensorFeatures.IrradiationTemperatureCompensated) != 0) {
    ret.push(createTagMetadata(Tags.Weather.TemperatureCompensatedRadiation.device(id), TagValueType.Float, "w/m²",
      "Irradiance PV", "Temperature compensated radiation", 0, 1500, TagSource.Storage));
  }

  if ((flag & WeatherSensorFeatures.TemperatureInstalledOnPanel) != 0) {
    ret.push(createTagMetadata(Tags.Weather.BackOfModuleTemp.device(id), TagValueType.Float, "°c",
      "Back Of Module Temp", "", -50, 50, TagSource.Storage));
  }

  if ((flag & WeatherSensorFeatures.RelativeHumidity) != 0) {
    ret.push(createTagMetadata(Tags.Weather.RelativeHumidity.device(id), TagValueType.Float, "%RH",
      "Relative Humidity", "", 0, 100, TagSource.Storage));
  }

  if ((flag & WeatherSensorFeatures.Barometric) != 0) {
    ret.push(createTagMetadata(Tags.Weather.BarometricPressure.device(id), TagValueType.Float, "%RH",
      "Barometric Pressure", "", 0, 100, TagSource.Storage));
  }

  if (includeErrors) {
    ret.push(createTagMetadata(Tags.Weather.ErrorType.device(id), TagValueType.Enum, "Enum",
      "Error flags", "", 0, 4294967300, TagSource.Storage));
  }

  return ret;
};

//prettier-ignore
const generateCalculatedWeatherTags = (maxLimit: number) =>
  [
    createTagMetadata(Tags.Portal.Weather.AggrStatus.logicalName, TagValueType.Enum, "", "Weather Status", "", 0, maxLimit, TagSource.Calculated),
    createTagMetadata(Tags.VirtualWeather.CalibratedGHI.logicalName, TagValueType.Float, "W/m²", "Irradiance", "Calibrated Global Horizontal Irradiance", 0, 1500, TagSource.Calculated),
    createTagMetadata(Tags.Portal.DailyIrradiance.CalibratedGHI.logicalName, TagValueType.Float, "Wh/m²", "cumulative Irradiance", "Cumulative Calibrated Global Horizontal Irradiance", 0, 10_000, TagSource.Calculated),
  ];

//prettier-ignore
const generateTagsForAgriDevice = (ret: ITagMetadata[], sensorId: number, sensorType: AgriSensorManufacturerType) => {
  switch (sensorType) {
    case AgriSensorManufacturerType.CwtSoil:
      ret.push(createTagMetadata(Tags.Agriculture.SoilTemperature.device(sensorId), TagValueType.Float, "Degrees",
        "Soil Temperature", "", 0, 100, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.SoilHumidity.device(sensorId), TagValueType.Float, " %RH",
        "Soil Humidity", "", 0, 100, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.SoilPH.device(sensorId), TagValueType.Float, "",
        "PH", "", 0, 100, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.SoilConductivity.device(sensorId), TagValueType.Float, "cm",
        "Soil Conductivity", "", 0, 20000, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.SoilNitrogen.device(sensorId), TagValueType.Float, "mg/kg(mg/L)",
        "Soil Nitrogen", "", 0, 3000, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.SoilPotassium.device(sensorId), TagValueType.Float, "mg/kg(mg/L)",
        "Soil Potassium", "", 0, 3000, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.SoilPhosphorus.device(sensorId), TagValueType.Float, "mg/kg(mg/L)",
        "Soil Phosphorus", "", 0, 3000, TagSource.Storage));
      break;
    case AgriSensorManufacturerType.CwtPhotoSynth:
      ret.push(createTagMetadata(Tags.Agriculture.PlantPhotosyntheticallyActiveRadiationPpfd.device(sensorId), TagValueType.Float, "μmol/㎡",
        "Daily Photosynthetic Active Radiation", "", 0, 2000, TagSource.Storage));
      break;
    case AgriSensorManufacturerType.HondeGrowthSensor:
    case AgriSensorManufacturerType.Dr3Growth:
      ret.push(createTagMetadata(Tags.Agriculture.StemGrowth.device(sensorId), TagValueType.Float, "mm",
        "Dendrometer(growth)", "", 0, 200, TagSource.Storage));
      break;
    case AgriSensorManufacturerType.CwtRainGauge:
      ret.push(createTagMetadata(Tags.Agriculture.RainFall.device(sensorId), TagValueType.Float, "mm",
        "Rain Gauge", "", 0, 8, TagSource.Storage));
      break;
    case AgriSensorManufacturerType.SenTecSoil:
      for (const [index, tag] of AgricultureTagGroups.humidityLayers.entries()) {
        const layer = index + 1;
        ret.push(createTagMetadata(tag.device(sensorId), TagValueType.Float,
           "%RH",
          `Soil Humidity Layer ${layer}`, "", 0, 100, TagSource.Storage));
      }
      for (const [index, tag] of AgricultureTagGroups.temperatureLayers.entries()) {
        const layer = index + 1;
        ret.push(createTagMetadata(tag.device(sensorId), TagValueType.Float,
           "°C",
          `Soil Temperature Layer ${layer}`, "", -30, 90, TagSource.Storage));
      }
      ret.push(createTagMetadata(Tags.Agriculture.SoilSalinity.device(sensorId), TagValueType.Float, "µS/cm",
        "The measured Soil Salinity", "", 0, 1000, TagSource.Storage));
      ret.push(createTagMetadata(Tags.Agriculture.PlantPhotosyntheticallyActiveRadiationPpfd.device(sensorId), TagValueType.Float, "μmol/㎡",
        "Daily Photosynthetic Active Radiation", "", 0, 2000, TagSource.Storage));
      break;
    default:
      throw new Error("received an invalid agri sensor");
  }
};

//prettier-ignore
const generateAlgoPerformanceTagMetadata = (aggregatedInverterPowerMax: number) => {
  return [
    createTagMetadata(Tags.Portal.PerformanceModel.PotentialPowerProduction.logicalName, TagValueType.Float, "kW",
      "Potential", "Potential Production Model", 0, aggregatedInverterPowerMax, TagSource.Calculated),
    createTagMetadata(Tags.Portal.PerformanceModel.ContractPowerProduction.logicalName, TagValueType.Float, "kW",
      "Contract", "", 0, aggregatedInverterPowerMax, TagSource.Calculated)
  ];
};

//prettier-ignore
const generateMcsTagMetadata = () =>
  [
    createTagMetadata(Tags.McsState.State.master, TagValueType.Enum, "", "Site State",
      "Undefined=0; Safety=1;Operational=2;", 0, 2, TagSource.Storage),
    createTagMetadata(Tags.McsState.EngineerEmergencyStatus.master, TagValueType.Enum, "", "Engineer Emergency Status",
      "Undefined=0; Active=1; Inactive=2; Unknown=3;", 0, 3, TagSource.Storage),
    createTagMetadata(Tags.McsState.CloudEmergencyStatus.master, TagValueType.Enum, "", "Cloud Emergency Status",
      "Undefined=0; Active=1; Inactive=2; Unknown=3;", 0, 3, TagSource.Storage),
    createTagMetadata(Tags.McsState.TcmFirmwareAutoUpdateMode.master, TagValueType.Enum, "", "TCM Firmware Auto Update Mode",
      "Undefined=0; Auto=1; Manual=2;", 0, 2, TagSource.Storage),
    createTagMetadata(Tags.McsState.TimeToNextAutoFWUpdate.master, TagValueType.Duration, "", "Time To Next Auto FW Update",
      "", 0, undefined, TagSource.Storage),
    createTagMetadata(Tags.Portal.System.FieldState.logicalName, TagValueType.Enum, "", "Field State",
      "State of the whole Tracker Field", 0, 18, TagSource.Calculated),
    createTagMetadata(Tags.Portal.System.SafetyReason.logicalName, TagValueType.Int, "", "Safety Reason",
      "Reason that site is in Safe mode", 0, 512, TagSource.Calculated),
  ];
